import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { GOOGLE_BUSINESS_CATEGORIES } from "@/lib/categories";
import {
  Search, Building2, Star, Globe, Plus, RefreshCw, MapPin,
  CheckSquare, Zap, Filter, ChevronDown, ChevronUp,
} from "lucide-react";
import { REVENUE_OPTIONS, EMPLOYEE_OPTIONS, RATING_OPTIONS } from "./options";

export function ProspectSearchView({
  keyword, setKeyword, city, setCity, state, setState, handleSearch, loading,
  showFilters, setShowFilters, activeFilterCount,
  selectedCategory, setSelectedCategory, selectedRevenue, setSelectedRevenue,
  selectedEmployees, setSelectedEmployees, minRating, setMinRating, toggleFilter, clearAllFilters,
  results, searched, selectedSearch, toggleSearchSelect, toggleAllSearch,
  savingIds, handleSaveSingle, handleBulkSave, bulkSaving,
}) {
  return (
    <>
      {/* Search bar */}
      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-3 space-y-2">
          <div className="flex gap-2 items-center">
            <Input data-testid="search-keyword" placeholder="Industry or keyword (e.g., Plumbing, HVAC)" value={keyword}
              onChange={(e) => setKeyword(e.target.value)} onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              className="h-9 text-sm flex-1" />
            <Input data-testid="search-city" placeholder="City" value={city}
              onChange={(e) => setCity(e.target.value)} className="h-9 text-sm w-32" />
            <Input data-testid="search-state" placeholder="State" value={state}
              onChange={(e) => setState(e.target.value)} className="h-9 text-sm w-20" />
            <Button data-testid="search-submit" onClick={handleSearch} disabled={loading}
              className="h-9 bg-indigo-600 hover:bg-indigo-700 text-white px-5">
              {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4 mr-1" />}
              Search
            </Button>
            <Button data-testid="toggle-filters" variant="outline" size="sm"
              onClick={() => setShowFilters(!showFilters)}
              className={`h-9 text-xs gap-1.5 ${activeFilterCount > 0 ? "border-indigo-300 bg-indigo-50 text-indigo-700" : ""}`}>
              <Filter className="w-3.5 h-3.5" />
              Filters {activeFilterCount > 0 && <Badge className="h-4 w-4 p-0 flex items-center justify-center text-[9px] bg-indigo-600 text-white">{activeFilterCount}</Badge>}
              {showFilters ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            </Button>
          </div>

          {/* Advanced filters — multi-select checkboxes */}
          {showFilters && (
            <div className="pt-2 border-t border-slate-100 animate-fade-in space-y-3">
              {/* Category — single-select dropdown (Google business categories) */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Category</Label>
                  {selectedCategory && <button onClick={() => setSelectedCategory("")} className="text-[10px] text-indigo-600 hover:underline">Clear</button>}
                </div>
                <Select value={selectedCategory || "all"} onValueChange={(v) => setSelectedCategory(v === "all" ? "" : v)}>
                  <SelectTrigger data-testid="category-select" className="h-8 text-xs w-full sm:w-72">
                    <SelectValue placeholder="All categories" />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    <SelectItem value="all">All categories</SelectItem>
                    {GOOGLE_BUSINESS_CATEGORIES.map((cat) => (
                      <SelectItem key={cat} value={cat} data-testid={`category-option-${cat.toLowerCase().replace(/\s+/g, "-")}`}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-3 gap-4">
                {/* Revenue */}
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Revenue {selectedRevenue.size > 0 && <Badge className="ml-1 h-4 w-4 p-0 text-[9px] bg-indigo-600 text-white inline-flex items-center justify-center">{selectedRevenue.size}</Badge>}</Label>
                    {selectedRevenue.size > 0 && <button onClick={() => setSelectedRevenue(new Set())} className="text-[10px] text-indigo-600 hover:underline">Clear</button>}
                  </div>
                  <div data-testid="revenue-checkboxes" className="space-y-1">
                    {REVENUE_OPTIONS.map((opt) => (
                      <label key={opt.value} className={`flex items-center gap-2 px-2 py-1 rounded border cursor-pointer text-[11px] transition-colors ${
                        selectedRevenue.has(opt.value) ? "bg-indigo-50 border-indigo-300 text-indigo-700 font-medium" : "bg-white border-slate-200 text-slate-600 hover:border-slate-300"
                      }`}>
                        <Checkbox
                          data-testid={`filter-rev-${opt.value}`}
                          checked={selectedRevenue.has(opt.value)}
                          onCheckedChange={() => toggleFilter(selectedRevenue, setSelectedRevenue, opt.value)}
                          className="w-3 h-3"
                        />
                        {opt.label}
                      </label>
                    ))}
                  </div>
                </div>

                {/* Company Size */}
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Company Size {selectedEmployees.size > 0 && <Badge className="ml-1 h-4 w-4 p-0 text-[9px] bg-indigo-600 text-white inline-flex items-center justify-center">{selectedEmployees.size}</Badge>}</Label>
                    {selectedEmployees.size > 0 && <button onClick={() => setSelectedEmployees(new Set())} className="text-[10px] text-indigo-600 hover:underline">Clear</button>}
                  </div>
                  <div data-testid="employee-checkboxes" className="space-y-1">
                    {EMPLOYEE_OPTIONS.map((opt) => (
                      <label key={opt.value} className={`flex items-center gap-2 px-2 py-1 rounded border cursor-pointer text-[11px] transition-colors ${
                        selectedEmployees.has(opt.value) ? "bg-indigo-50 border-indigo-300 text-indigo-700 font-medium" : "bg-white border-slate-200 text-slate-600 hover:border-slate-300"
                      }`}>
                        <Checkbox
                          data-testid={`filter-emp-${opt.value}`}
                          checked={selectedEmployees.has(opt.value)}
                          onCheckedChange={() => toggleFilter(selectedEmployees, setSelectedEmployees, opt.value)}
                          className="w-3 h-3"
                        />
                        {opt.label} employees
                      </label>
                    ))}
                  </div>
                </div>

                {/* Min Rating */}
                <div>
                  <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1.5 block">Min Rating</Label>
                  <Select value={minRating} onValueChange={setMinRating}>
                    <SelectTrigger data-testid="search-rating" className="h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>{RATING_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>

              {activeFilterCount > 0 && (
                <div className="flex justify-end">
                  <Button data-testid="clear-all-filters" variant="ghost" size="sm" onClick={clearAllFilters} className="h-6 text-[10px] text-slate-500">
                    Clear all filters
                  </Button>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Bulk bar */}
      {selectedSearch.size > 0 && (
        <div className="flex items-center gap-3 p-2.5 bg-indigo-50 border border-indigo-200 rounded-lg animate-fade-in">
          <CheckSquare className="w-4 h-4 text-indigo-600" />
          <span className="text-xs font-medium text-indigo-700">{selectedSearch.size} selected</span>
          <Button data-testid="bulk-save-btn" size="sm" onClick={handleBulkSave} disabled={bulkSaving}
            className="h-7 text-xs bg-indigo-600 hover:bg-indigo-700 text-white ml-auto">
            {bulkSaving ? <RefreshCw className="w-3 h-3 animate-spin mr-1" /> : <Zap className="w-3 h-3 mr-1" />}
            Save & Audit ({selectedSearch.size})
          </Button>
        </div>
      )}

      {/* Results table */}
      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50 h-9">
                <TableHead className="w-8 pl-3"><Checkbox data-testid="select-all-search" checked={results.length > 0 && selectedSearch.size === results.length} onCheckedChange={toggleAllSearch} /></TableHead>
                <TableHead className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Company</TableHead>
                <TableHead className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Industry</TableHead>
                <TableHead className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Location</TableHead>
                <TableHead className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Revenue</TableHead>
                <TableHead className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Size</TableHead>
                <TableHead className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Rating</TableHead>
                <TableHead className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Website</TableHead>
                <TableHead className="text-[10px] font-bold uppercase tracking-wider text-slate-500 w-16"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {results.length > 0 ? results.map((r, i) => (
                <TableRow key={i} className={`prospect-row h-11 ${selectedSearch.has(i) ? "bg-indigo-50/40" : ""}`} data-testid={`search-result-${i}`}>
                  <TableCell className="pl-3"><Checkbox checked={selectedSearch.has(i)} onCheckedChange={() => toggleSearchSelect(i)} /></TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded bg-slate-100 flex items-center justify-center shrink-0"><Building2 className="w-3.5 h-3.5 text-slate-400" /></div>
                      <div>
                        <p className="text-[13px] font-medium leading-tight flex items-center gap-1.5">
                          {r.name}
                          {r.source === "web" && (
                            <span data-testid={`source-web-${i}`} title="Found via web search — often service-area/low-visibility businesses Google Maps hides. Details fill in after you save."
                              className="text-[8px] font-bold uppercase tracking-wide px-1 py-0.5 rounded bg-indigo-100 text-indigo-700">Web</span>
                          )}
                        </p>
                        <p className="text-[10px] text-muted-foreground">{r.phone}</p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell><Badge variant="outline" className="text-[10px] h-5">{r.category}</Badge></TableCell>
                  <TableCell><span className="text-[11px] text-muted-foreground flex items-center gap-0.5"><MapPin className="w-3 h-3" /> {r.city}, {r.state}</span></TableCell>
                  <TableCell><span className="text-[11px] font-medium">{r.revenue_label || "--"}</span></TableCell>
                  <TableCell><span className="text-[11px] text-muted-foreground">{r.employee_count || "--"} emp</span></TableCell>
                  <TableCell>
                    {r.google_rating ? (
                      <div className="flex items-center gap-1">
                        <Star className="w-3 h-3 text-amber-400 fill-amber-400" />
                        <span className="text-xs font-medium">{r.google_rating}</span>
                        <span className="text-[10px] text-muted-foreground">({r.google_reviews})</span>
                      </div>
                    ) : <span className="text-[10px] text-slate-400">--</span>}
                  </TableCell>
                  <TableCell>
                    {r.has_website ? <span className="text-[10px] text-teal-600 font-medium flex items-center gap-0.5"><Globe className="w-3 h-3" />{r.domain?.slice(0, 18)}</span>
                      : <span className="text-[10px] text-slate-400">None</span>}
                  </TableCell>
                  <TableCell>
                    <Button data-testid={`save-prospect-${i}`} size="sm" variant="outline"
                      disabled={savingIds.has(i)} onClick={() => handleSaveSingle(r, i)} className="h-6 text-[10px] px-2">
                      {savingIds.has(i) ? <RefreshCw className="w-3 h-3 animate-spin" /> : <><Plus className="w-3 h-3 mr-0.5" /> Save</>}
                    </Button>
                  </TableCell>
                </TableRow>
              )) : (
                <TableRow><TableCell colSpan={9} className="text-center py-10 text-sm text-muted-foreground">
                  {searched ? "No results. Try broader criteria." : "Enter a keyword and search to find prospects."}
                </TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </>
  );
}
